import {
  AdjustmentImpactEnum,
  AdjustmentScopeEnum,
  BookingTypeEnum,
} from '@koob/enums';

/**
 * KD-3327: the scope of an adjustment is carried by its impact, never by its
 * target. "Specific products" is the only scope that picks a side of the
 * price — the margin or the purchase price — so any other impact covers the
 * whole booking or the whole trip.
 *
 * `targetType` says which of the two it covers, and which route edits it; it
 * is not the scope: a product adjustment targets the booking that carries it.
 */
export const SPECIFIC_PRODUCT_IMPACTS = [
  AdjustmentImpactEnum.MARGIN,
  AdjustmentImpactEnum.PURCHASE_PRICE,
];

export const isSpecificProductAdjustment = (adjustment) =>
  adjustment?.scope === AdjustmentScopeEnum.PRODUCT ||
  SPECIFIC_PRODUCT_IMPACTS.includes(adjustment?.impact);

// An API that predates the impact split sends no impact. Such an adjustment
// behaves like the former ones, which all bore on the whole target.
export const isEntireScopeAdjustment = (adjustment) =>
  adjustment?.scope
    ? adjustment.scope !== AdjustmentScopeEnum.PRODUCT
    : !isSpecificProductAdjustment(adjustment);

// An API that predates the impact split sends no `allowedImpacts`: both
// impacts stay available and the API decides.
export const isImpactAllowed = (allowedImpacts, impact) =>
  !Array.isArray(allowedImpacts) || allowedImpacts.includes(impact);

// An empty list is the API's answer for a product with no side of its price
// to pick, such as a booking the DMC made itself: it bears on its selling
// price only.
export const hasImpactChoice = (allowedImpacts) =>
  !Array.isArray(allowedImpacts) || allowedImpacts.length > 0;

// Never emit an impact the product forbids: an explicit choice survives only
// while it stays allowed, otherwise the remaining impact wins.
export const resolveAdjustmentImpact = (allowedImpacts, impact) => {
  if (impact && isImpactAllowed(allowedImpacts, impact)) {
    return impact;
  }

  return isImpactAllowed(allowedImpacts, AdjustmentImpactEnum.MARGIN)
    ? AdjustmentImpactEnum.MARGIN
    : AdjustmentImpactEnum.PURCHASE_PRICE;
};

/**
 * KD-3327: a booking page adjusts a single product, the booking itself, so
 * the impact alone carries the scope.
 *
 * The selling price is never sent — the API reads that scope from the absence
 * of `impactsByBooking` — and PATCH only takes an impact when the adjustment
 * already bears on one side of the price, a scope it refuses to change.
 */
export const getBookingImpactPayload = ({
  bookingId,
  impact,
  allowedImpacts,
  isEditing = false,
}) => {
  if (!isSpecificProductAdjustment({ impact })) {
    return { impact: undefined, impactsByBooking: undefined };
  }

  const resolved = resolveAdjustmentImpact(allowedImpacts, impact);

  return {
    impact: isEditing ? resolved : undefined,
    impactsByBooking: [{ bookingId, impact: resolved }],
  };
};

/**
 * Names what the adjustment covers: the product for a "Specific products"
 * item, the scope it spans otherwise. Returns undefined when a product
 * adjustment reaches us without its product name, so the caller can leave the
 * line untitled rather than claim a scope the adjustment does not have.
 */
export const getAdjustmentScopeLabel = (adjustment, t) => {
  const productName = adjustment?.name ?? adjustment?.productName;

  if (isSpecificProductAdjustment(adjustment)) {
    return productName;
  }

  if (
    adjustment?.scope === AdjustmentScopeEnum.ENTIRE_TRIP ||
    adjustment?.targetType === BookingTypeEnum.TRIP
  ) {
    return t('entireTrip', 'Entire trip');
  }

  return t('entireBooking', 'Entire booking');
};
